/**
 * Villages, after vanilla's jigsaw villages: a meeting point (the well) at the
 * centre, dirt-path streets out to the edges, and buildings lining the streets
 * with their doors on the road. Each building is a blueprint drawn with style
 * roles ('wall', 'log', 'roof', ...), and the biome picks the style — the same
 * five palettes vanilla has (plains, desert, savanna, taiga, snowy).
 *
 * To add a building: draw a blueprint (front row = the street side, door on
 * it facing +v), add it to BUILDINGS with a weight. Roofs are added by code
 * (`roof`), so blueprints stop at the top of the walls.
 */

import { SEA_LEVEL, CHUNK_SIZE } from '../../constants';
import { AIR, IS_OPAQUE, SHAPE_FAMILIES, shapeId, WATER_ID } from '../../blocks';
import { hash2 } from '../../noise';
import { Biome } from '../biomes';
import { getBlock, pcol, setBlock } from '../ctx';
import type { TerrainGenerator } from '../terrain';
import {
  block, type Blueprint, blueprint, type Builder, cell, chance, F, footprint, int, piece, type Piece,
  type Rng, type Roles, seedOf, type StructureType, toWorld, worldPiece,
} from './kit';

// --- styles ------------------------------------------------------------------------

interface Style {
  roles: Roles;
  /** Gabled roof of `roof` blocks, or a flat one (desert). */
  roofShape: 'gable' | 'flat';
  path: number;
  /** Path speckle (gravel in green villages). */
  pathAlt: number;
}

function style(
  wall: string, log: string, roof: string, base: string, floor: string, door: string,
  roofShape: 'gable' | 'flat', path: string, pathAlt: string, accent: string,
): Style {
  return {
    roles: {
      wall: block(wall), log: block(log), roof: block(roof), base: block(base), floor: block(floor),
      accent: block(accent), glass: block('Glass'),
      door: cell.door(door, F.PV),
    },
    roofShape, path: block(path), pathAlt: block(pathAlt),
  };
}

const PLAINS = style('Oak Planks', 'Oak Log', 'Spruce Planks', 'Cobblestone', 'Oak Planks', 'Oak Door',
  'gable', 'Dirt Path', 'Gravel', 'White Wool');
const DESERT = style('Smooth Sandstone', 'Cut Sandstone', 'Smooth Sandstone', 'Sandstone', 'Sandstone', 'Oak Door',
  'flat', 'Smooth Sandstone', 'Sandstone', 'Terracotta');
const SAVANNA = style('Acacia Planks', 'Acacia Log', 'Acacia Planks', 'Cobblestone', 'Acacia Planks', 'Oak Door',
  'gable', 'Dirt Path', 'Coarse Dirt', 'Terracotta');
const TAIGA = style('Spruce Planks', 'Spruce Log', 'Spruce Planks', 'Cobblestone', 'Spruce Planks', 'Spruce Door',
  'gable', 'Dirt Path', 'Gravel', 'Mossy Cobblestone');
const SNOWY = style('Spruce Planks', 'Spruce Log', 'Spruce Planks', 'Polished Diorite', 'Spruce Planks', 'Spruce Door',
  'gable', 'Dirt Path', 'Snow Block', 'Packed Ice');

function styleOf(b: Biome): Style | null {
  switch (b) {
    case Biome.Plains: case Biome.SunflowerPlains: case Biome.Meadow: return PLAINS;
    case Biome.Desert: return DESERT;
    case Biome.Savanna: case Biome.SavannaPlateau: return SAVANNA;
    case Biome.Taiga: case Biome.OldGrowthPineTaiga: case Biome.OldGrowthSpruceTaiga: return TAIGA;
    case Biome.SnowyPlains: case Biome.SnowyTaiga: return SNOWY;
    default: return null;
  }
}

/** Biomes villages (and pillager outposts) can appear in. */
export const hasVillageStyle = (b: Biome): boolean => styleOf(b) !== null;

// --- buildings ---------------------------------------------------------------------

// Shared key: style roles plus the furniture every house uses. Front row is the
// street side; the door ('D') is drawn on it, its upper half left as ' '.
const KEY = {
  B: 'base', F: 'floor', W: 'wall', L: 'log', G: 'glass', D: 'door', A: 'accent', R: 'roof',
  C: cell.chest(F.NV), T: block('Crafting Table'), K: block('Bookshelf'), U: cell.furnace(F.NV),
  E: cell.bed(F.PV), H: block('Hay Bale'), '*': block('Torch'), M: block('Smooth Stone'),
  V: block('Lava'), '~': WATER_ID, Q: block('Farmland'), w: block('Wheat'), O: block('Cobblestone'),
  P: block('Pumpkin'), Y: block('Melon'),
} as const;

// Job-site blocks: a villager claims one of these and takes the matching
// profession (entities/village/villager.ts, game/trading.ts WORKSTATIONS).
const JOB = {
  m: block('Composter'), l: block('Lectern'), r: block('Barrel'), z: block('Fletching Table'),
  g: block('Grindstone'), a: block('Blast Furnace'), s: block('Smoker'), n: block('Stonecutter'),
  t: block('Smithing Table'), b: block('Brewing Stand'),
} as const;
const KEYJ = { ...KEY, ...JOB } as const;

// The torch stands on the floor beside the chest, never on top of it (a chest
// is not a full block, so a torch cannot sit on it — it blocked the chest click).
const SMALL_HOUSE = blueprint(KEY, [
  ['BBBBB', 'BFFFB', 'BFFFB', 'BFFFB', 'BBBBB'],
  ['LWDWL', 'W...W', 'WT.EW', 'WC* W', 'LWWWL'],
  ['LG GL', 'W...W', 'G...G', 'W...W', 'LWGWL'],
  ['LWWWL', 'W...W', 'W...W', 'W...W', 'LWWWL'],
]);

/** A small house whose crafting table is swapped for a job-site block (`site` = a JOB key). */
const workHouse = (site: keyof typeof JOB): Blueprint => blueprint(KEYJ, [
  ['BBBBB', 'BFFFB', 'BFFFB', 'BFFFB', 'BBBBB'],
  ['LWDWL', 'W...W', `W${site}.EW`, 'WC* W', 'LWWWL'],
  ['LG GL', 'W...W', 'G...G', 'W...W', 'LWGWL'],
  ['LWWWL', 'W...W', 'W...W', 'W...W', 'LWWWL'],
]);
const FISHER = workHouse('r');
const FLETCHER = workHouse('z');
const MASON = workHouse('n');
const CLERIC = workHouse('b');

const HOUSE = blueprint(KEY, [
  ['BBBBBBB', 'BFFFFFB', 'BFFFFFB', 'BFFFFFB', 'BFFFFFB', 'BBBBBBB'],
  ['LWWDWWL', 'W.....W', 'WT...EW', 'WC... W', 'WU*.*CW', 'LWWWWWL'],
  ['LGW WGL', 'W.....W', 'G.....G', 'W.....W', 'W.....W', 'LWGWGWL'],
  ['LWWWWWL', 'W.....W', 'W.....W', 'W.....W', 'W.....W', 'LWWWWWL'],
]);

const LIBRARY = blueprint(KEYJ, [
  ['BBBBBBBBB', 'BFFFFFFFB', 'BFFFFFFFB', 'BFFFFFFFB', 'BFFFFFFFB', 'BFFFFFFFB', 'BBBBBBBBB'],
  ['LWWWDWWWL', 'WK.....KW', 'WK.....KW', 'WK..l..KW', 'WK..*..KW', 'WKKKCKKKW', 'LWWWWWWWL'],
  ['LGGW WGGL', 'WK.....KW', 'G*.....*G', 'WK.....KW', 'G.......G', 'WKKKKKKKW', 'LWGGWGGWL'],
  ['LWWWWWWWL', 'WK.....KW', 'W.......W', 'WK.....KW', 'W.......W', 'WKKKKKKKW', 'LWWWWWWWL'],
  ['LWWWWWWWL', 'W.......W', 'W.......W', 'W.......W', 'W.......W', 'W.......W', 'LWWWWWWWL'],
]);

// Weaponsmith: a covered work yard open to the street, forge in the back corner.
const SMITHY = blueprint(KEYJ, [
  ['BBBBBBBB', 'BMMMMFFB', 'BMMMMFFB', 'BMMMMFFB', 'BOOOOFFB', 'BBBBBBBB'],
  ['L...LDWL', 'Mg..L..W', 't...W.CW', 'U...WE.W', 'OV*aW .W', 'LOOOLWWL'],
  ['L...L WL', '....L..W', '....W..G', '....W..W', 'O...W..W', 'LOOOLWGL'],
  ['LLLLLLLL', 'L......L', 'L......L', 'L......L', 'L......L', 'LLLLLLLL'],
]);

// Butcher: a small shop with a smoker (furnace) and a fenced yard behind.
const BUTCHER = blueprint(KEYJ, [
  ['BBBBBBB', 'BFFFFFB', 'BFFFFFB', 'BFFFFFB', 'BBBBBBB'],
  ['LWWDWWL', 'W.....W', 'Ws*.*TW', 'WCH...W', 'LWWWWWL'],
  ['LGW WGL', 'W.....W', 'G.....G', 'W.....W', 'LWGGGWL'],
  ['LWWWWWL', 'W.....W', 'W.....W', 'W.....W', 'LWWWWWL'],
]);

const FARM = blueprint(KEYJ, [
  ['LLLLLLL', 'LQQ~QQL', 'LQQ~QQL', 'LQQ~QQL', 'LQQ~QQL', 'LQQ~QQL', 'LQQ~QQL', 'LQQ~QQL', 'LLLLLLL'],
  ['m      ', ' ww ww ', ' ww ww ', ' ww ww ', ' ww ww ', ' ww ww ', ' ww ww ', ' ww ww ', '       '],
]);

const LARGE_FARM = blueprint(KEYJ, [
  ['LLLLLLLLLLLLL', 'LQQ~QQLQQ~QQL', 'LQQ~QQLQQ~QQL', 'LQQ~QQLQQ~QQL', 'LQQ~QQLQQ~QQL', 'LQQ~QQLQQ~QQL',
    'LQQ~QQLQQ~QQL', 'LQQ~QQLQQ~QQL', 'LLLLLLLLLLLLL'],
  ['m            ', ' ww ww  ww PY', ' ww ww  ww ww', ' ww ww  ww ww', ' ww ww  ww ww', ' ww ww  ww ww',
    ' ww ww  ww ww', ' ww ww  ww ww', '             '],
]);

// Animal pen: a wooden fence around hay.
const PEN = blueprint({ ...KEY, N: block('Oak Fence') }, [
  ['NNN.NNN', 'N.....N', 'N.H...N', 'N...H.N', 'N.....N', 'NNNNNNN'],
  ['.......', '.......', '.......', '.......', '.......', '.......'],
]);

interface Building {
  bp: Blueprint;
  weight: number;
  /** Gets a roof (fields and pens do not). */
  roofed: boolean;
}

/** What lines the streets, and how often. */
const BUILDINGS: readonly Building[] = [
  { bp: SMALL_HOUSE, weight: 5, roofed: true },
  { bp: HOUSE, weight: 4, roofed: true },
  { bp: LIBRARY, weight: 1, roofed: true },
  { bp: SMITHY, weight: 1, roofed: true },
  { bp: BUTCHER, weight: 1, roofed: true },
  { bp: FISHER, weight: 1, roofed: true },
  { bp: FLETCHER, weight: 1, roofed: true },
  { bp: MASON, weight: 1, roofed: true },
  { bp: CLERIC, weight: 1, roofed: true },
  { bp: FARM, weight: 3, roofed: false },
  { bp: LARGE_FARM, weight: 1, roofed: false },
  { bp: PEN, weight: 1, roofed: false },
];
const TOTAL_WEIGHT = BUILDINGS.reduce((s, b) => s + b.weight, 0);

function pickBuilding(rnd: Rng): Building {
  let r = rnd() * TOTAL_WEIGHT;
  for (const b of BUILDINGS) if ((r -= b.weight) < 0) return b;
  return BUILDINGS[0];
}

/**
 * Roof over a w x d building whose walls end at `top`: a gable running front to
 * back (stairs in vanilla, full blocks here) with a 1-block overhang and the
 * gable ends closed in wall, or a flat slab with a parapet for desert houses.
 */
function roof(b: Builder, s: Style, w: number, d: number, top: number): void {
  const R = s.roles.roof, W = s.roles.wall;
  if (s.roofShape === 'flat') {
    b.fill(0, top + 1, 0, w - 1, top + 1, d - 1, R);
    b.walls(0, top + 2, 0, w - 1, top + 2, d - 1, s.roles.log);
    return;
  }
  // stairs of the roof material when it has them (rising toward the ridge), else full blocks
  const fam = SHAPE_FAMILIES.find((f) => f.kind === 'stair' && f.base === R);
  const up = (facing: number): typeof R => (fam ? shapeId(fam, { facing: b.facing(facing) }) : R);
  for (let i = 0; ; i++) {
    const a = i - 1, c = w - i;
    if (a > c) break;
    const y = top + 1 + i;
    for (let v = -1; v <= d; v++) { b.set(a, y, v, a === c ? R : up(1)); b.set(c, y, v, a === c ? R : up(3)); }
    for (let u = a + 1; u < c; u++) { b.set(u, y, 0, W); b.set(u, y, d - 1, W); }
  }
}

/** Build one blueprint building on its lot: clear, found, stamp, roof. */
function build(b: Builder, s: Style, bld: Building): void {
  const { w, d, h } = bld.bp;
  const y = b.y;
  b.fill(-1, y + 1, -1, w, y + h + w, d, AIR);
  b.foundations(0, 0, w - 1, d - 1, y - 1, s.roles.base, 6);
  b.stamp(bld.bp, 0, y, 0, s.roles);
  if (bld.roofed) roof(b, s, w, d, y + h - 1);
}

// --- the meeting point -------------------------------------------------------------

// Vanilla's plains well: a cobblestone ring of water under a four-post roof.
const WELL = blueprint(KEY, [
  ['OOOOOO', 'OOOOOO', 'OO~~OO', 'OO~~OO', 'OOOOOO', 'OOOOOO'],
  ['      ', ' OOOO ', ' O..O ', ' O..O ', ' OOOO ', '      '],
  ['      ', ' L..L ', ' .... ', ' .... ', ' L..L ', '      '],
  ['      ', ' L..L ', ' .... ', ' .... ', ' L..L ', '      '],
  ['      ', ' RRRR ', ' RRRR ', ' RRRR ', ' RRRR ', '      '],
]);

function meetingPoint(b: Builder, s: Style): void {
  const y = b.y;
  b.fill(-2, y + 1, -2, 7, y + 6, 7, AIR);
  b.fill(-2, y, -2, 7, y, 7, s.path);
  b.fill(2, y - 3, 2, 3, y - 1, 3, WATER_ID);
  b.foundations(0, 0, 5, 5, y - 4, s.roles.base, 4);
  b.stamp(WELL, 0, y, 0, s.roles);
  // lamp posts on two corners of the plaza
  for (const [u, v] of [[-2, -2], [7, 7]]) {
    b.fill(u, y + 1, v, u, y + 2, v, s.roles.log);
    b.set(u, y + 3, v, KEY['*']);
  }
}

// --- streets ---------------------------------------------------------------------

/** A street: re-surfaces the terrain it crosses (bridging water with planks). */
function street(x0: number, z0: number, x1: number, z1: number, y: number, s: Style): Piece {
  return worldPiece(x0, z0, x1, z1, y, (ctx) => {
    for (let z = Math.max(z0, ctx.oz); z <= Math.min(z1, ctx.oz + CHUNK_SIZE - 1); z++) {
      for (let x = Math.max(x0, ctx.ox); x <= Math.min(x1, ctx.ox + CHUNK_SIZE - 1); x++) {
        const g = ctx.top[pcol(ctx, x, z)];
        if (g < SEA_LEVEL) { setBlock(ctx, x, SEA_LEVEL, z, s.roles.floor as number); continue; }
        setBlock(ctx, x, g, z, hash2(x, z, ctx.seed ^ 0x9a7) < 0.12 ? s.pathAlt : s.path);
        // clear plants and stray leaves off the road
        for (let yy = g + 1; yy <= g + 3; yy++) {
          if (IS_OPAQUE[Math.max(0, getBlock(ctx, x, yy, z))] === 0) setBlock(ctx, x, yy, z, AIR);
        }
      }
    }
  });
}

// --- layout ------------------------------------------------------------------------

function village(g: TerrainGenerator, cx: number, cz: number, rnd: Rng): Piece[] | null {
  const centre = g.columnClimate(cx, cz);
  const s = styleOf(centre.biome);
  if (!s || centre.height < SEA_LEVEL + 1) return null;
  const pieces: Piece[] = [];
  type Rect = { x0: number; z0: number; x1: number; z1: number };
  const lots: Rect[] = [];
  const free = (r: Rect): boolean =>
    lots.every((o) => r.x1 + 2 < o.x0 || r.x0 - 2 > o.x1 || r.z1 + 2 < o.z0 || r.z0 - 2 > o.z1);

  const wy = centre.height;
  pieces.push(piece(cx - 3, cz - 3, 0, 6, 6, wy, true, seedOf(rnd), (b) => meetingPoint(b, s)));
  lots.push({ x0: cx - 6, z0: cz - 6, x1: cx + 6, z1: cz + 6 });

  for (let dir = 0; dir < 4; dir++) {
    const dx = [1, -1, 0, 0][dir], dz = [0, 0, 1, -1][dir];
    const len = 26 + int(rnd, 30);
    const ex = cx + dx * len, ez = cz + dz * len;
    // 3-wide street from the plaza outward
    pieces.push(street(Math.min(cx, ex) - (dz !== 0 ? 1 : 0), Math.min(cz, ez) - (dx !== 0 ? 1 : 0),
      Math.max(cx, ex) + (dz !== 0 ? 1 : 0), Math.max(cz, ez) + (dx !== 0 ? 1 : 0), wy, s));
    for (let t = 8; t < len - 2; t += 3 + int(rnd, 3)) {
      for (const side of [1, -1]) {
        if (chance(rnd, 0.25)) continue;
        const bld = pickBuilding(rnd);
        const { w, d } = bld.bp;
        // local +v points away from the street, so the front row (and door) faces it
        const rot = dx !== 0 ? (side === 1 ? 0 : 2) : (side === 1 ? 3 : 1);
        const frontX = cx + dx * t + (dx !== 0 ? 0 : side * 3);
        const frontZ = cz + dz * t + (dz !== 0 ? 0 : side * 3);
        // centre the building on the street point: local origin is w/2 back along u
        const [x, z] = toWorld(frontX, frontZ, rot, -(w >> 1), 0);
        const fp = footprint(x, z, rot, w, d);
        if (!free(fp)) continue;
        const here = g.columnClimate((fp.x0 + fp.x1) >> 1, (fp.z0 + fp.z1) >> 1);
        if (here.height < SEA_LEVEL + 1 || Math.abs(here.height - wy) > 10) continue;
        lots.push(fp);
        pieces.push(piece(x, z, rot, w, d, here.height, true, seedOf(rnd), (b) => build(b, s, bld)));
        // a lamp post beside some fronts
        if (chance(rnd, 0.2)) {
          const [lx, lz] = toWorld(frontX, frontZ, rot, (w >> 1) + 2, -1);
          pieces.push(piece(lx, lz, 0, 1, 1, here.height, false, 0, (b) => {
            b.fill(0, b.y + 1, 0, 0, b.y + 2, 0, s.roles.log);
            b.set(0, b.y + 3, 0, KEY['*']);
          }));
        }
      }
    }
  }
  return pieces.length > 6 ? pieces : null;
}

export const VILLAGE: StructureType = {
  name: 'Village', spacing: 34, separation: 8, salt: 10387312, reach: 5, generate: village,
};

